import { PersonMapping, personMappingOptions } from '@bp/shared-planung-utils';
import { SliderModal } from '@bp/ui-components';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../../hooks/useConfirm';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { XlsxImportDropper } from '../components/XlsxImportDropper';
import { XlsxTeacherImportForm } from '../forms/XlsxTeacherImportForm';
import { XlsxTeacherMappingForm } from '../forms/XlsxTeacherMappingForm';
import { XlsxFileImport, XlsxImportColumn, XlsxImportEntry } from '../types/XlsxImportTypes';
import { getPersonMappingLabel } from '../utils/XlsxTranslationUtils';
import styles from './_ImportModals.module.scss';

type XlsxTeacherImportModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const XlsxTeacherImportModal = ({ isOpen, onClose }: XlsxTeacherImportModalProps) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState<boolean>(false);
  const [index, setIndex] = useState<number>(0);
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const [dirty, setDirty] = useState<boolean>(false);
  const [fileImport, setFileImport] = useState<XlsxFileImport | null>(null);
  const [columns, setColumns] = useState<XlsxImportColumn<PersonMapping>[]>([]);
  const [entries, setEntries] = useState<XlsxImportEntry<PersonMapping>[]>([]);

  const { teacherData } = useLoadBasicData({ pause: !fileImport });
  const profiles = teacherData.people ?? [];

  const { ConfirmationDialog, confirm } = useConfirm({
    defaultTitle: t('confirm.abort.title'),
    defaultConfirmText: t('confirm.abort.confirm'),
    defaultMessage: t('confirm.abort.message'),
    defaultCancelText: t('confirm.abort.cancel'),
  });

  async function handleClose() {
    if (dirty) {
      const result = await confirm();
      if (result) reset();
    } else {
      reset();
    }
  }

  function reset() {
    setCurrentIndex(null);
    setIndex(0);
    setFileImport(null);
    setDirty(false);
    onClose();
  }

  const handleFileLoaded = useCallback((fileImport: XlsxFileImport) => {
    setFileImport(fileImport);
    setCurrentIndex(1);
    setIndex(1);
  }, []);

  // slider components
  const FileUpload: ReactNode = useMemo(() => {
    return <XlsxImportDropper onLoad={setLoading} onFileLoaded={handleFileLoaded} className={styles.dropper} />;
  }, [handleFileLoaded]);

  const ImportForm: ReactNode = useMemo(() => {
    return (
      fileImport && (
        <XlsxTeacherImportForm
          fileImport={fileImport}
          initialColumns={columns}
          onColumnsChanged={(columns) => {
            setColumns(columns);

            const newEntries: XlsxImportEntry<PersonMapping>[] = fileImport.rows.map((row, index) => {
              // new entry object with values from personMappingOptions as keys
              const entry: Record<PersonMapping, string | null> = personMappingOptions.reduce(
                (acc, option) => ({ ...acc, [option]: null }),
                {} as Record<PersonMapping, string | null>,
              );

              // iterate personMappingOptions and find value from row for each entry
              personMappingOptions.forEach((option) => {
                const index = columns.find(({ content }) => content === option)?.index ?? null;
                entry[option] = index || index === 0 ? row[index] : null;
              });

              // find already existing person
              const selectedProfile = profiles.find((p) => p.displayName === `${entry.firstName} ${entry.lastName}`);

              return { ...entry, rowIndex: index, connectProfileUuid: selectedProfile?.uuid ?? null };
            });

            setEntries(newEntries);
            setDirty(true);
          }}
        />
      )
    );
  }, [fileImport, columns, profiles]);

  const MappingForm: ReactNode = useMemo(() => {
    return (
      <XlsxTeacherMappingForm
        initialEntries={entries}
        onEntriesChanged={(entries) => {
          setEntries(entries);
        }}
      />
    );
  }, [entries]);

  // next/previous logic
  const canViewImportForm = !!fileImport;

  const canViewMappingForm =
    !!columns.find((o) => o.content === 'firstName')?.index && !!columns.find((o) => o.content === 'lastName')?.index;

  const nextDisabled = index === 0 ? !canViewImportForm : index === 1 ? !canViewMappingForm : false;

  // submit
  async function handleSubmit() {
    console.log(entries);
    // TODO: implement
  }

  // initial values
  useEffect(() => {
    if (fileImport) {
      const initialColumns: XlsxImportColumn<PersonMapping>[] = fileImport
        ? personMappingOptions.map((column) => {
            const translatedLabel = getPersonMappingLabel(column);
            const index = fileImport.headers.findIndex((h) => h === translatedLabel);
            return {
              content: column,
              index: index !== undefined && index > -1 ? index : null,
            };
          })
        : [];

      const initialEntries: XlsxImportEntry<PersonMapping>[] = fileImport.rows.map((row, index) => {
        // new entry object with values from personMappingOptions as keys
        const entry: Record<PersonMapping, string | null> = personMappingOptions.reduce(
          (acc, option) => ({ ...acc, [option]: null }),
          {} as Record<PersonMapping, string | null>,
        );

        // iterate personMappingOptions and find value from row for each entry
        personMappingOptions.forEach((option) => {
          const index = initialColumns.find(({ content }) => content === option)?.index ?? null;
          entry[option] = index || index === 0 ? row[index] : null;
        });

        // find already existing person
        const selectedProfile = profiles.find((p) => p.displayName === `${entry.firstName} ${entry.lastName}`);

        return { ...entry, rowIndex: index, connectProfileUuid: selectedProfile?.uuid ?? null };
      });

      setColumns(initialColumns);
      setEntries(initialEntries);
      setFileImport(fileImport);
    }
  }, [fileImport, profiles]);

  return (
    <>
      <SliderModal
        className={styles['import-modal']}
        isOpen={isOpen}
        loading={loading}
        title={t('import.importType', { type: t('persons.title', { count: 2 }) })}
        submitText={t('import.import')}
        modalSettings={{ width: 'l' }}
        slideClassName={styles.slide}
        slides={[FileUpload, ImportForm, MappingForm]}
        defaultIndex={currentIndex ?? undefined}
        onClose={() => handleClose()}
        onNext={() => setIndex(index + 1)}
        onPrevious={() => setIndex(index - 1)}
        nextDisabled={nextDisabled}
        submitDisabled={index !== 2 || !canViewMappingForm}
        onSubmit={() => handleSubmit()}
      />

      <ConfirmationDialog />
    </>
  );
};
